import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { generateServiceSchema, generateOrganizationSchema, generateFAQSchema, generateBreadcrumbSchema, injectSchema } from '@/lib/swissSchema';
import { updateMetaTags } from '@/utils/seoCore';
import { generateHreflangLinks, updateHreflangLinks } from '@/lib/swissHreflang';
import { SwissLanguage, getCityDisplayName } from '@/lib/swissI18n';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Play, CheckCircle, Users, TrendingUp, Clock, Zap } from 'lucide-react';

const AIVideoService = () => {
  const { city } = useParams();
  const { currentLanguage } = useLanguage();
  const language = currentLanguage as SwissLanguage;
  
  const cityDisplayName = city ? getCityDisplayName(language, city) : null;
  const isHubPage = !city;
  
  // Content based on language
  const content = {
    de: {
      title: isHubPage 
        ? 'AI-Video & Performance Video Ads – KI-Videos, die verkaufen'
        : `AI-Video & Werbevideos ${cityDisplayName} – Mehr Conversions in 14 Tagen`,
      description: isHubPage
        ? 'KI-gestützte Videos für Social Media, Produktpräsentationen und Performance Ads. Schnelle Produktion, A/B-Testing, mehrsprachige Untertitel. Messbare Ergebnisse in 14 Tagen.'
        : `Professionelle KI-Videos und Werbevideos in ${cityDisplayName}. Schnelle Produktion, A/B-Testing, Untertitel DE/FR/IT/EN. Jetzt kostenloses Konzept sichern.`,
      h1: isHubPage
        ? 'AI-Video & Performance Video Ads'
        : `AI-Video ${cityDisplayName}`,
      subtitle: isHubPage
        ? 'KI-Videos, die verkaufen – in 14 Tagen produktionsreif'
        : `Mehr Conversions in 14 Tagen – KI-Videos aus ${cityDisplayName}`,
      hero: 'Ihre Videos müssen nicht nur schön aussehen – sie müssen verkaufen. Mit unserer KI-gestützten Videoproduktion erstellen wir in 14 Tagen Videos, die messbar mehr Conversions generieren.',
      
      packages: [
        {
          name: 'Video Starter',
          price: 'CHF 2\'500',
          features: [
            '1 AI-Video (60 Sekunden)',
            'Untertitel DE/EN',
            'A/B-Testing Setup',
            '2 Revisionsrunden',
            'Performance Report'
          ]
        },
        {
          name: 'Video Growth',
          price: 'CHF 4\'900',
          features: [
            '3 AI-Videos + Varianten',
            'Untertitel DE/FR/IT/EN',
            'Social Media Adaptionen',
            'Landing Page Integration',
            'Conversion Tracking',
            'Monatliches Reporting'
          ],
          popular: true
        },
        {
          name: 'Video Dominator',
          price: 'CHF 8\'500',
          features: [
            '5 AI-Videos + A/B-Tests',
            'Vollständige Ad-Kampagne',
            'Retargeting Videos',
            'Performance Optimierung',
            'Quarterly Business Review',
            '90 Tage Support'
          ]
        }
      ],
      
      process: [
        { step: 1, title: 'Konzept & Briefing', desc: 'Zielgruppe, Botschaft und KPIs definieren' },
        { step: 2, title: 'AI-Produktion', desc: 'Video-Erstellung mit KI-gestützten Tools' },
        { step: 3, title: 'Optimierung', desc: 'A/B-Testing und Performance-Analyse' },
        { step: 4, title: 'Launch & Scale', desc: 'Rollout über alle Kanäle und Skalierung' },
        { step: 5, title: 'Reporting', desc: 'Messbare Ergebnisse und ROI-Analyse' }
      ],
      
      caseStudy: {
        title: 'Case Study: SaaS-Startup',
        metrics: [
          { label: 'Conversion Rate', before: '2.1%', after: '8.7%' },
          { label: 'CPL (Cost per Lead)', before: 'CHF 85', after: 'CHF 23' },
          { label: 'Video View Rate', before: '12%', after: '67%' }
        ]
      },
      
      faq: [
        {
          question: 'Wie schnell können AI-Videos produziert werden?',
          answer: 'Erste Videos sind in 3-5 Tagen fertig. Komplette Kampagnen mit A/B-Tests und Optimierung in 14 Tagen.'
        },
        {
          question: 'Welche Sprachen werden für Untertitel unterstützt?',
          answer: 'Standardmässig DE/EN. Erweitert: FR/IT/ES für den europäischen Markt. Automatische Synchronisation verfügbar.'
        },
        {
          question: `Was kostet AI-Video Produktion in ${cityDisplayName || 'der Schweiz'}?`,
          answer: 'Einzelvideos ab CHF 2\'500. Kampagnen mit mehreren Videos und A/B-Testing ab CHF 4\'900. ROI garantiert.'
        },
        {
          question: 'Wie unterscheiden sich AI-Videos von klassischer Produktion?',
          answer: '10x schneller, 5x günstiger, unbegrenzte Varianten für A/B-Tests. Datengetrieben statt nur kreativ.'
        },
        {
          question: 'Können Videos für verschiedene Plattformen angepasst werden?',
          answer: 'Ja. Automatische Formate für Instagram, LinkedIn, YouTube, TikTok, Facebook Ads. Optimiert für jede Plattform.'
        },
        {
          question: 'Wie wird der ROI gemessen?',
          answer: 'Conversion Tracking, A/B-Testing Resultate, Cost-per-Lead Vergleiche. Transparente Reportings mit klaren KPIs.'
        }
      ]
    }
    // Add FR/IT/EN variants later
  };

  const currentContent = content[language] || content.de;
  
  useEffect(() => {
    // Update meta tags
    updateMetaTags({
      title: currentContent.title,
      description: currentContent.description,
      canonicalUrl: `https://www.future-level.ch/${language}/ai-video${city ? `/${city}` : ''}`,
      language: language,
      keywords: `AI Video, KI Videos, Performance Ads, Video Marketing, ${cityDisplayName || 'Schweiz'}`
    });

    // Update hreflang
    const hreflangLinks = generateHreflangLinks({
      currentLanguage: language,
      currentPath: `/ai-video${city ? `/${city}` : ''}`,
      availableLanguages: ['de', 'fr', 'it', 'en']
    });
    updateHreflangLinks(hreflangLinks);

    // Inject schemas
    const orgSchema = generateOrganizationSchema({ language });
    const serviceSchema = generateServiceSchema({
      serviceName: 'AI-Video Produktion',
      serviceSlug: 'ai-video',
      city,
      language,
      priceRange: 'CHF 2500-8500',
      description: currentContent.description,
      url: `https://www.future-level.ch/${language}/ai-video${city ? `/${city}` : ''}`
    });
    const breadcrumbSchema = generateBreadcrumbSchema(language, 'ai-video', city);
    const faqSchema = generateFAQSchema({ 
      questions: currentContent.faq, 
      language 
    });

    injectSchema({
      "@context": "https://schema.org",
      "@graph": [orgSchema, serviceSchema, breadcrumbSchema, faqSchema]
    });
  }, [language, city, cityDisplayName]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-900">
      <Header />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-24 overflow-hidden">
          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-6 bg-purple-600/20 text-purple-200 border-purple-500/30">
                <Zap className="w-4 h-4 mr-2" />
                KI-Video Spezialist {cityDisplayName && `• ${cityDisplayName}`}
              </Badge>
              
              <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                {currentContent.h1}
                <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent block">
                  {currentContent.subtitle}
                </span>
              </h1>
              
              <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
                {currentContent.hero}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" className="bg-purple-600 hover:bg-purple-700 text-white">
                  <Play className="w-5 h-5 mr-2" />
                  Kostenloses Video-Konzept
                </Button>
                <Button size="lg" variant="outline" className="border-purple-500/30 text-purple-200 hover:bg-purple-900/20">
                  Portfolio ansehen
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Packages Section */}
        <section className="py-20 bg-black/20">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                Transparente Pakete
              </h2>
              <p className="text-gray-300 text-lg max-w-2xl mx-auto">
                Klare Preise, messbare Ergebnisse. Wählen Sie das Paket, das zu Ihren Zielen passt.
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {currentContent.packages.map((pkg, index) => (
                <Card key={index} className={`relative bg-gradient-to-b from-slate-800/50 to-slate-900/50 border-slate-700 ${pkg.popular ? 'ring-2 ring-purple-500' : ''}`}>
                  {pkg.popular && (
                    <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-purple-600 text-white">
                      Beliebt
                    </Badge>
                  )}
                  <CardHeader>
                    <CardTitle className="text-white text-xl">{pkg.name}</CardTitle>
                    <CardDescription className="text-3xl font-bold text-purple-400">
                      {pkg.price}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {pkg.features.map((feature, fIndex) => (
                        <li key={fIndex} className="flex items-center text-gray-300">
                          <CheckCircle className="w-5 h-5 text-green-400 mr-3 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <Button className="w-full mt-6 bg-purple-600 hover:bg-purple-700">
                      Paket wählen
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                Unser Prozess
              </h2>
              <p className="text-gray-300 text-lg">
                Von der Idee zum Performance-Video in 14 Tagen
              </p>
            </div>
            
            <div className="max-w-4xl mx-auto">
              {currentContent.process.map((step, index) => (
                <div key={index} className="flex items-start mb-8 last:mb-0">
                  <div className="flex-shrink-0 w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold mr-6">
                    {step.step}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">{step.title}</h3>
                    <p className="text-gray-300">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Case Study Section */}
        <section className="py-20 bg-gradient-to-r from-purple-900/20 to-pink-900/20">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-bold text-white mb-8">
                {currentContent.caseStudy.title}
              </h2>
              
              <div className="grid md:grid-cols-3 gap-8 mb-8">
                {currentContent.caseStudy.metrics.map((metric, index) => (
                  <Card key={index} className="bg-black/40 border-purple-500/30">
                    <CardContent className="p-6 text-center">
                      <h3 className="text-lg font-semibold text-white mb-4">{metric.label}</h3>
                      <div className="flex justify-between items-center">
                        <div className="text-red-400">
                          <div className="text-sm">Vorher</div>
                          <div className="text-xl font-bold">{metric.before}</div>
                        </div>
                        <TrendingUp className="text-green-400 w-6 h-6" />
                        <div className="text-green-400">
                          <div className="text-sm">Nachher</div>
                          <div className="text-xl font-bold">{metric.after}</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-white text-center mb-12">
                Häufige Fragen
              </h2>
              
              <div className="space-y-6">
                {currentContent.faq.map((item, index) => (
                  <Card key={index} className="bg-slate-800/50 border-slate-700">
                    <CardContent className="p-6">
                      <h3 className="text-lg font-semibold text-white mb-3">{item.question}</h3>
                      <p className="text-gray-300">{item.answer}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-purple-600 to-pink-600">
          <div className="container mx-auto px-6 text-center">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
                Bereit für Videos, die verkaufen?
              </h2>
              <p className="text-xl text-purple-100 mb-8">
                Kostenloses Strategiegespräch und Video-Konzept für Ihr Unternehmen{cityDisplayName && ` in ${cityDisplayName}`}.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" className="bg-white text-purple-600 hover:bg-gray-100">
                  <Clock className="w-5 h-5 mr-2" />
                  Termin buchen (15 Min)
                </Button>
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                  <Users className="w-5 h-5 mr-2" />
                  Kostenloses Audit
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default AIVideoService;